class SihaCategoryCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.scroller = this.querySelector('[data-siha-category-scroller]');
    this.previousButton = this.querySelector('[data-siha-category-previous]');
    this.nextButton = this.querySelector('[data-siha-category-next]');
    this.progress = this.querySelector('[data-siha-category-progress]');
    this.progressThumb = this.querySelector('[data-siha-category-progress-thumb]');
    this.cards = [...this.querySelectorAll('[data-siha-category-card]')];

    if (!this.scroller || !this.cards.length) return;

    this.initialized = true;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handlePrevious = this.handlePrevious.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.update = this.update.bind(this);

    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.scroller.addEventListener('scroll', this.handleScroll, { passive: true });
    this.scroller.addEventListener('keydown', this.handleKeydown);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.scroller);
    }

    requestAnimationFrame(this.update);
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.scroller?.removeEventListener('scroll', this.handleScroll);
    this.scroller?.removeEventListener('keydown', this.handleKeydown);
    this.resizeObserver?.disconnect();
    window.cancelAnimationFrame(this.scrollFrame);
    this.initialized = false;
  }

  get maximumScroll() {
    return Math.max(this.scroller.scrollWidth - this.scroller.clientWidth, 0);
  }

  get step() {
    const firstCard = this.cards[0];
    const gap = Number.parseFloat(window.getComputedStyle(this.scroller).gap) || 0;
    return firstCard.getBoundingClientRect().width + gap;
  }

  move(direction) {
    const target = Math.min(
      this.maximumScroll,
      Math.max(0, this.scroller.scrollLeft + this.step * direction)
    );

    this.scroller.scrollTo({
      left: target,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  handlePrevious() {
    this.move(-1);
  }

  handleNext() {
    this.move(1);
  }

  handleKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.move(-1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.move(1);
    }
  }

  handleScroll() {
    window.cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = window.requestAnimationFrame(this.update);
  }

  update() {
    const maximum = this.maximumScroll;
    const current = Math.min(Math.max(this.scroller.scrollLeft, 0), maximum);
    const hasOverflow = maximum > 4;

    if (this.previousButton) {
      this.previousButton.hidden = !hasOverflow;
      this.previousButton.disabled = current <= 2;
    }

    if (this.nextButton) {
      this.nextButton.hidden = !hasOverflow;
      this.nextButton.disabled = current >= maximum - 2;
    }

    this.updateProgress(current, maximum, hasOverflow);
    this.updateActiveCard();
  }

  updateProgress(current, maximum, hasOverflow) {
    if (!this.progress || !this.progressThumb) return;

    this.progress.hidden = !hasOverflow;
    if (!hasOverflow) return;

    const visibleRatio = Math.min(this.scroller.clientWidth / this.scroller.scrollWidth, 1);
    const progressRatio = maximum > 0 ? current / maximum : 0;
    const thumbWidth = visibleRatio * 100;
    const availableTravel = 100 - thumbWidth;

    this.progressThumb.style.width = `${thumbWidth}%`;
    this.progressThumb.style.left = `${progressRatio * availableTravel}%`;
  }

  updateActiveCard() {
    const scrollerBox = this.scroller.getBoundingClientRect();
    const viewportCenter = scrollerBox.left + scrollerBox.width / 2;
    let activeCard;
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.cards.forEach((card) => {
      const cardBox = card.getBoundingClientRect();
      const cardCenter = cardBox.left + cardBox.width / 2;
      const distance = Math.abs(cardCenter - viewportCenter);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        activeCard = card;
      }
    });

    this.cards.forEach((card) => card.classList.toggle('is-active', card === activeCard));
  }
}

if (!customElements.get('siha-category-carousel')) {
  customElements.define('siha-category-carousel', SihaCategoryCarousel);
}


